import { useRef } from 'react';
import { useJobRefetch } from '@hooks/job/refetch';
import { queryFetch } from '@services/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@utils/consts';
import {
  applyOptimisticScheduleDrop,
  OptimisticScheduleDrop,
  PreviousScheduleQueriesData,
  restoreScheduleQueriesData,
} from '@services/workload/calendar/helpers';
import { pauseRevalidation, resumeRevalidation } from '@services/revalidateQueries';
import { WorkloadCalendarRespSuccess } from '@services/workload/calendar/types';
import { InternalScheduleReq, InternalScheduleRespSuccess } from './types';

type CreateInternalScheduleVariables = InternalScheduleReq & {
  optimisticDropData?: OptimisticScheduleDrop;
};

export const useCreateInternalSchedule = ({
  id,
  callback,
}: {
  id: string;
  callback: (data: InternalScheduleRespSuccess) => void;
}) => {
  const { refetch } = useJobRefetch({ schedule: { type: 'internal', action: 'create' } });
  const queryClient = useQueryClient();
  const previousDataRef = useRef<PreviousScheduleQueriesData | null>(undefined);

  const { mutate, isPending } = useMutation({
    mutationFn: ({
      optimisticDropData: _optimisticDropData,
      ...data
    }: CreateInternalScheduleVariables): Promise<InternalScheduleRespSuccess> =>
      queryFetch({ url: `jobs/${id}/internal_schedules`, data, opt: { method: 'POST' } }),
    onMutate: async ({ optimisticDropData }) => {
      if (!optimisticDropData) return;
      pauseRevalidation(queryKeys.workloadWeeklyCalendar);
      await queryClient.cancelQueries({ queryKey: queryKeys.workloadWeeklyCalendar });
      const previousData = queryClient.getQueriesData<WorkloadCalendarRespSuccess>({
        queryKey: queryKeys.workloadWeeklyCalendar,
      });
      previousDataRef.current = previousData;
      queryClient.setQueriesData<WorkloadCalendarRespSuccess>(
        { queryKey: queryKeys.workloadWeeklyCalendar },
        (old) => applyOptimisticScheduleDrop(old, optimisticDropData),
      );
      return { previousData };
    },
    onError: (err, _variables, context) => {
      console.error(err);
      restoreScheduleQueriesData(queryClient, context?.previousData);
      previousDataRef.current = undefined;
      resumeRevalidation(queryKeys.workloadWeeklyCalendar);
    },
    onSuccess: async ({ success }) => {
      if (!success) return;
      previousDataRef.current = undefined;
      resumeRevalidation(queryKeys.workloadWeeklyCalendar);
      await refetch();
    },
    onSettled: (data) => {
      if (data) callback(data);
    },
  });

  const discardOptimisticDropDataUpdate = async () => {
    if (previousDataRef.current) {
      restoreScheduleQueriesData(queryClient, previousDataRef.current);
      previousDataRef.current = undefined;
    }
    resumeRevalidation(queryKeys.workloadWeeklyCalendar);
    await refetch();
  };

  return {
    mutate,
    isLoading: isPending,
    discardOptimisticDropDataUpdate,
  };
};
